/**
 * 平台会话 token：
 * 主路径：memory ↔ localStorage（原有逻辑保留）
 * 二次兜底：Cookie（LS 读空 / 写失败时仍尽量跨刷新保会话）
 * 中长期：后端 HttpOnly Cookie + withCredentials。
 */
import { probeLocalStorage } from '@/utils/client-storage';
import { readAuthCookie, removeAuthCookie, writeAuthCookie } from '@/utils/auth-cookie';

const TOKEN_KEY = 'vantag-access-token';
const REFRESH_KEY = 'vantag-refresh-token';
const USER_ID_KEY = 'vantag-user-id';
const WALLET_ADDRESS_KEY = 'vantag-wallet-address';
const WALLET_PROVIDER_KEY = 'vantag-wallet-provider';
/** EMAIL | GOOGLE | WALLET — 登录入口；平台钱包可能仍是 privy */
const ORIGIN_LOGIN_TYPE_KEY = 'vantag-origin-login-type';
/** 钱包登录时用的插件 id（metamask/okx…），供 Connected 充值 */
const LOGIN_WALLET_PROVIDER_KEY = 'vantag-login-wallet-provider';
const LOGGED_IN_KEY = 'vantag-demo-logged-in';
const TOKEN_EXPIRES_AT_KEY = 'vantag-token-expires-at';

/** 参与 Cookie 兜底的必要键（写 LS 同时尽力写 Cookie） */
const COOKIE_FALLBACK_KEYS = new Set([
  TOKEN_KEY,
  REFRESH_KEY,
  USER_ID_KEY,
  WALLET_ADDRESS_KEY,
  WALLET_PROVIDER_KEY,
  ORIGIN_LOGIN_TYPE_KEY,
  LOGIN_WALLET_PROVIDER_KEY,
  LOGGED_IN_KEY,
  TOKEN_EXPIRES_AT_KEY,
]);

const ORIGIN_LOGIN_TYPES = new Set(['EMAIL', 'GOOGLE', 'WALLET']);

/** 提前 30 秒视为过期，避免临界请求失败 */
const EXPIRY_SKEW_MS = 30_000;

/** @type {Record<string, string>} */
const memoryStore = Object.create(null);

/** 最近一次写入未能持久化到 localStorage，且 Cookie 也未兜住 */
let authStorageEphemeral = false;

function readLs(key) {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string|null|undefined} value  null/undefined → remove
 * @returns {boolean} 是否写入 localStorage 成功
 */
function writeLs(key, value) {
  try {
    if (typeof localStorage === 'undefined') return false;
    if (value == null || value === '') {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
    return true;
  } catch {
    return false;
  }
}

function cookieMaxAgeSecFor(key, value) {
  if (key === TOKEN_EXPIRES_AT_KEY && value) {
    const expiresAt = Number(value);
    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
      return Math.max(60, Math.floor((expiresAt - Date.now()) / 1000));
    }
  }
  if (key === TOKEN_KEY && value) {
    const expMs = decodeJwtExpMs(value);
    if (expMs && expMs > Date.now()) {
      return Math.max(60, Math.floor((expMs - Date.now()) / 1000));
    }
  }
  return 60 * 60 * 24 * 7;
}

/**
 * Cookie 二次兜底写；失败静默，不影响主路径。
 * @returns {boolean}
 */
function writeCookieFallback(key, value) {
  if (!COOKIE_FALLBACK_KEYS.has(key)) return false;
  if (value == null || value === '') {
    return removeAuthCookie(key);
  }
  return writeAuthCookie(key, String(value), {
    maxAgeSec: cookieMaxAgeSecFor(key, String(value)),
  });
}

function readValue(key) {
  if (Object.prototype.hasOwnProperty.call(memoryStore, key) && memoryStore[key] != null) {
    return String(memoryStore[key]);
  }
  // 主路径：localStorage
  const fromLs = readLs(key);
  if (fromLs != null && fromLs !== '') {
    memoryStore[key] = fromLs;
    return fromLs;
  }
  // 二次兜底：Cookie（不删 LS 逻辑；仅 LS 空时回补）
  if (COOKIE_FALLBACK_KEYS.has(key)) {
    const fromCookie = readAuthCookie(key);
    if (fromCookie != null && fromCookie !== '') {
      memoryStore[key] = fromCookie;
      // 能写回 LS 则回填主路径，保持两侧一致
      writeLs(key, fromCookie);
      return fromCookie;
    }
  }
  return '';
}

/**
 * @param {string} key
 * @param {string|null|undefined} value
 * @returns {{ lsOk: boolean, cookieOk: boolean }}
 */
function writeValue(key, value) {
  if (value == null || value === '') {
    delete memoryStore[key];
    const lsOk = writeLs(key, null);
    const cookieOk = writeCookieFallback(key, null);
    return { lsOk, cookieOk };
  }
  const str = String(value);
  memoryStore[key] = str;
  const lsOk = writeLs(key, str);
  // 始终尝试 Cookie 兜底（LS 成功时也写一份，便于 LS 之后被清仍可恢复）
  const cookieOk = writeCookieFallback(key, str);
  return { lsOk, cookieOk };
}

/**
 * @param {Array<{ lsOk: boolean, cookieOk: boolean }>} results
 */
function markEphemeralFromWrites(results) {
  // LS 或 Cookie 任一落盘成功 → 非纯 ephemeral
  authStorageEphemeral = !results.some((r) => r.lsOk || r.cookieOk);
}

/** 当前会话是否仅 memory（关页/刷新会丢） */
export function isAuthSessionEphemeral() {
  if (authStorageEphemeral) return true;
  const hasToken = Boolean(getAccessToken());
  if (!hasToken) return false;
  if (probeLocalStorage().ok) return false;
  // LS 不可用但 Cookie 仍可读 → 不算 ephemeral
  if (readAuthCookie(TOKEN_KEY)) return false;
  return true;
}

function decodeJwtExpMs(token) {
  try {
    const raw = String(token || '')
      .trim()
      .replace(/^bearer\s+/i, '');
    const parts = raw.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    const exp = Number(payload.exp);
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** 去掉误存的 Bearer 前缀，统一为裸 JWT */
export function normalizeAccessToken(value) {
  return String(value ?? '')
    .trim()
    .replace(/^bearer\s+/i, '');
}

export function getAccessToken() {
  return normalizeAccessToken(readValue(TOKEN_KEY) || '');
}

export function getRefreshToken() {
  return readValue(REFRESH_KEY) || '';
}

export function getUserId() {
  return readValue(USER_ID_KEY) || '';
}

export function getTokenExpiresAt() {
  const cached = readValue(TOKEN_EXPIRES_AT_KEY);
  if (cached) {
    const n = Number(cached);
    if (Number.isFinite(n)) return n;
  }
  return decodeJwtExpMs(getAccessToken());
}

/**
 * 缓存 access token 过期时间。
 * 后端 expiresIn 单位为分钟（如 720 = 12 小时），非 OAuth 常见的秒。
 */
export function cacheTokenExpiry(accessToken, expiresInMinutes) {
  let expiresAt = null;
  if (expiresInMinutes != null && expiresInMinutes !== '') {
    const minutes = Number(expiresInMinutes);
    if (Number.isFinite(minutes) && minutes > 0) {
      expiresAt = Date.now() + minutes * 60 * 1000;
    }
  }
  if (!expiresAt) {
    expiresAt = decodeJwtExpMs(accessToken);
  }
  const result = expiresAt
    ? writeValue(TOKEN_EXPIRES_AT_KEY, String(expiresAt))
    : writeValue(TOKEN_EXPIRES_AT_KEY, null);
  markEphemeralFromWrites([result]);
}

export function isTokenExpired() {
  const token = getAccessToken();
  if (!token) return true;
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) return false;
  return Date.now() >= expiresAt - EXPIRY_SKEW_MS;
}

export function setTokens(accessToken, refreshToken, userId) {
  const normalizedAccess = normalizeAccessToken(accessToken);
  const results = [];
  if (normalizedAccess) {
    results.push(writeValue(TOKEN_KEY, normalizedAccess));
    let expiresAt = decodeJwtExpMs(normalizedAccess);
    if (expiresAt) {
      results.push(writeValue(TOKEN_EXPIRES_AT_KEY, String(expiresAt)));
    }
  }
  if (refreshToken) results.push(writeValue(REFRESH_KEY, refreshToken));
  if (userId != null && userId !== '') {
    results.push(writeValue(USER_ID_KEY, String(userId)));
  }
  results.push(writeValue(LOGGED_IN_KEY, '1'));
  markEphemeralFromWrites(results);
}

export function getWalletAddress() {
  return readValue(WALLET_ADDRESS_KEY) || '';
}

export function setWalletAddress(address) {
  const value = String(address ?? '').trim();
  const result = writeValue(WALLET_ADDRESS_KEY, value || null);
  if (!result.lsOk && !result.cookieOk && value) authStorageEphemeral = true;
}

export function getWalletProviderId() {
  return readValue(WALLET_PROVIDER_KEY) || '';
}

export function setWalletProviderId(providerId) {
  const value = String(providerId ?? '')
    .trim()
    .toLowerCase();
  const result = writeValue(WALLET_PROVIDER_KEY, value || null);
  if (!result.lsOk && !result.cookieOk && value) authStorageEphemeral = true;
}

/** @returns {'EMAIL'|'GOOGLE'|'WALLET'|''} */
export function getOriginLoginType() {
  const value = String(readValue(ORIGIN_LOGIN_TYPE_KEY) ?? '')
    .trim()
    .toUpperCase();
  return ORIGIN_LOGIN_TYPES.has(value) ? value : '';
}

/** @param {string} [originLoginType] EMAIL | GOOGLE | WALLET */
export function setOriginLoginType(originLoginType) {
  const value = String(originLoginType ?? '')
    .trim()
    .toUpperCase();
  const result = ORIGIN_LOGIN_TYPES.has(value)
    ? writeValue(ORIGIN_LOGIN_TYPE_KEY, value)
    : writeValue(ORIGIN_LOGIN_TYPE_KEY, null);
  if (!result.lsOk && !result.cookieOk && ORIGIN_LOGIN_TYPES.has(value)) {
    authStorageEphemeral = true;
  }
}

/** 钱包登录时的插件 id（metamask / okx / …） */
export function getLoginWalletProviderId() {
  return String(readValue(LOGIN_WALLET_PROVIDER_KEY) ?? '')
    .trim()
    .toLowerCase();
}

export function setLoginWalletProviderId(providerId) {
  const value = String(providerId ?? '')
    .trim()
    .toLowerCase();
  const toStore = value && value !== 'privy' ? value : null;
  const result = writeValue(LOGIN_WALLET_PROVIDER_KEY, toStore);
  if (!result.lsOk && !result.cookieOk && toStore) authStorageEphemeral = true;
}

export function clearTokens() {
  const keys = [
    TOKEN_KEY,
    REFRESH_KEY,
    USER_ID_KEY,
    WALLET_ADDRESS_KEY,
    WALLET_PROVIDER_KEY,
    ORIGIN_LOGIN_TYPE_KEY,
    LOGIN_WALLET_PROVIDER_KEY,
    LOGGED_IN_KEY,
    TOKEN_EXPIRES_AT_KEY,
  ];
  for (const key of keys) {
    delete memoryStore[key];
    writeLs(key, null);
    if (COOKIE_FALLBACK_KEYS.has(key)) removeAuthCookie(key);
  }
  authStorageEphemeral = false;
}

export function hasAuthSession() {
  return Boolean(getAccessToken()) && !isTokenExpired();
}

/** 从登录接口 data 中提取并持久化 token（写失败不抛，走 memory / Cookie 兜底） */
export function persistLoginResponse(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid login response');
  }
  const accessToken = data.accessToken ?? data.token ?? data.access_token ?? data.jwt ?? null;
  const refreshToken = data.refreshToken ?? data.refresh_token ?? null;
  const userId = data.userId ?? data.id ?? null;
  if (!accessToken) {
    throw new Error('Login response missing token');
  }
  const normalizedAccess = normalizeAccessToken(accessToken);
  setTokens(normalizedAccess, refreshToken || normalizedAccess, userId);
  const expiresIn = data.expiresIn ?? data.expireIn ?? data.expires_in ?? data.tokenExpire ?? null;
  cacheTokenExpiry(normalizedAccess, expiresIn);
  const walletAddress =
    data.walletAddress ?? data.wallet ?? data.address ?? data.ethAddress ?? null;
  if (walletAddress) setWalletAddress(walletAddress);
  return data;
}
